import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';

export class PerpendicularBisectorConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; size?: number } = {}): ConstructionStep[] {
    const startX = options.x ?? 250;
    const startY = options.y ?? 400;
    const size = options.size ?? 300;
    const r = size * 0.7; // Radius > size/2

    const pA = { x: startX, y: startY };
    const pB = { x: startX + size, y: startY };
    const pMid = { x: startX + size / 2, y: startY };

    const h = Math.sqrt(r * r - (size / 2) * (size / 2));
    const pTop = { x: pMid.x, y: pMid.y - h };
    const pBot = { x: pMid.x, y: pMid.y + h };

    const pointA = new Point(this.renderer, pA.x, pA.y, 'A');
    const pointB = new Point(this.renderer, pB.x, pB.y, 'B');
    const pointMid = new Point(this.renderer, pMid.x, pMid.y, 'M');
    const baseSegment = new Segment(this.renderer, pA, pB, false);

    // arcA1: center A, pTop is at -44.4° from A. Sweep CCW from -18° to -72° (span 54°)
    const arcA1 = new Arc(this.renderer, pA, r, -Math.PI * 0.1, -Math.PI * 0.4, true, false);
    const arcA2 = new Arc(this.renderer, pA, r, Math.PI * 0.1, Math.PI * 0.4, true, true);

    // arcB1: center B, pTop is at -135.6° from B. Sweep CCW from -108° to -162° (span 54°)
    const arcB1 = new Arc(this.renderer, pB, r, -Math.PI * 0.6, -Math.PI * 0.9, true, false);
    const arcB2 = new Arc(this.renderer, pB, r, Math.PI * 0.6, Math.PI * 0.9, true, true);

    const pointTop = new Point(this.renderer, pTop.x, pTop.y, 'P1');
    const pointBot = new Point(this.renderer, pBot.x, pBot.y, 'P2');
    const bisector = new Segment(this.renderer, { x: pMid.x, y: pTop.y - 30 }, { x: pMid.x, y: pBot.y + 30 }, false);
    const rightAngleMark = new Angle(this.renderer, pMid, pB, pTop, '90°', true);

    [pointA, pointB, pointMid, pointTop, pointBot, baseSegment, bisector, arcA1, arcA2, arcB1, arcB2, rightAngleMark].forEach((o) =>
      o.setVisible(false)
    );

    return [
      {
        id: 'step1',
        title: '1. Base Segment AB',
        description: 'Draw base segment AB to be bisected.',
        narration: 'Step 1: Drawing baseline segment AB.',
        duration: 1200,
        animations: [new DrawPoint(pointA), new DrawPoint(pointB), new DrawLine(baseSegment, this.ruler)],
      },
      {
        id: 'step2',
        title: '2. Place Compass at A & Draw Arcs',
        description: 'Open compass to radius > AB/2. Draw upper and lower arcs centered at A.',
        narration: 'Step 2: Drawing top and bottom arcs centered at point A.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pA, r),
          new DrawArc(arcA1, this.compass),
          new DrawArc(arcA2, this.compass),
        ],
      },
      {
        id: 'step3',
        title: '3. Move to B & Draw Intersecting Arcs',
        description: 'With same radius, draw arcs centered at B to form intersection points P1 and P2.',
        narration: 'Step 3: Moving compass to point B and drawing intersecting arcs.',
        duration: 2000,
        animations: [
          new MoveCompass(this.compass, pB, r),
          new DrawArc(arcB1, this.compass),
          new DrawPoint(pointTop),
          new DrawArc(arcB2, this.compass),
          new DrawPoint(pointBot),
        ],
      },
      {
        id: 'step4',
        title: '4. Draw Perpendicular Bisector Line',
        description: 'Connect P1 and P2 using ruler to construct perpendicular bisector.',
        narration: 'Step 4: Drawing line through P1 and P2 to complete perpendicular bisector.',
        duration: 1500,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(bisector, this.ruler),
          new DrawPoint(pointMid),
        ],
      },
      {
        id: 'step5',
        title: '5. Highlight Right Angle & Midpoint',
        description: 'Highlight midpoint M and 90° angle.',
        narration: 'Construction complete! Segment AB is bisected at midpoint M.',
        duration: 1200,
        animations: [new Fade(this.ruler, false), new DrawPoint(rightAngleMark as any), new Highlight(bisector)],
      },
    ];
  }
}
